import { useEffect, useState } from "react"
import TicketList from "../ticket/TicketList"
import CharacterHistoryList from "../character/CharacterHistoryList"
import PunishmentList from "../punishment/PunishmentList"
import ClientList from "../client/ClientList"
import AddressList from "../address/AddressList"
import SharedAddressList from "../address/SharedAddressList"
import PointsList from "../points/PointsList"
import { useTime } from "../../time/Time"
import { useParams } from "react-router-dom"
import { fetchAndSetData, fetchData } from "../../api/Axios"
import { Autocomplete } from "@thaborach/react-autocomplete"
import { Chart, registerables } from "chart.js"
import { Bar } from "react-chartjs-2"
import { experienceAndReputation, formatDuration } from "../activity/activity"

Chart.register(...registerables)

const WEEK_MILLIS = 7 * 24 * 60 * 60 * 1000

// Eigenes Vorschlagsmenue: seit @thaborach/react-autocomplete 1.1.0 rendert das Standardmenue ein <select> mit
// <div>-Kindern - ein Browser zeigt davon nichts an, die Vorschlaege waren nur noch im DOM (#2023). Das Menue
// haengt unter dem Eingabefeld (der Wrapper der Bibliothek ist position: relative).
const suggestionMenuStyle = {
    position: "absolute",
    top: "100%",
    left: 0,
    minWidth: "100%",
    zIndex: 1000,
    borderRadius: "3px",
    boxShadow: "0 2px 12px rgba(0, 0, 0, 0.1)",
    background: "white",
    padding: "2px 0",
    fontSize: "90%",
    overflow: "auto",
    maxHeight: "50vh",
}

const renderSuggestionMenu = (items) => <div style={suggestionMenuStyle}>{items}</div>

const PlayerSearch = () => {
    const [player, setPlayer] = useState("")
    const [users, setUsers] = useState([])
    const [isLoaded, setIsLoaded] = useState(false)
    const [onlinetime, setOnlinetime] = useState(0)
    const { time: playTime } = useTime(onlinetime, 4)
    const [participantData, setParticipantData] = useState([])
    const [characterData, setCharacterData] = useState([])
    const [deaths, setDeaths] = useState(0)
    const [kills, setKills] = useState(0)
    const [mobKills, setMobKills] = useState(0)
    const [solvedQuests, setSolvedQuests] = useState(0)
    const [solvedActivities, setSolvedActivities] = useState(0)

    const [averageTimePerDay, setAverageTimePerDay] = useState(0)
    const { time: averagePlayTimePerDay } = useTime(averageTimePerDay, 4)
    const [lastWeekTime, setLastWeekTime] = useState(0)
    const [reputation, setReputation] = useState(undefined)
    const [experience, setExperience] = useState([])

    let optionalPlayer = useParams()
    useEffect(() => {
        if(optionalPlayer.player != null) {
            setPlayer(optionalPlayer.player)
            loadPlayer(optionalPlayer.player)
        }
    }, [optionalPlayer])
    
    useEffect(() => {
        if (
            onlinetime > 0 &&
            characterData &&
            characterData.current &&
            characterData.current.first_played
        ) {
            const seconds = Number(onlinetime)

            // how many real-world days have passed since first join?
            // clamp the min value to total days played (so we can't exceed 24h/day)
            const playedDays = seconds / (60 * 60 * 24)

            const daysActive = Math.max(
                playedDays,
                Math.floor((Date.now() - Number(characterData.current.first_played)) / (1000 * 60 * 60 * 24))
            );

            // average daily play time in seconds
            const avg = seconds / daysActive
            setAverageTimePerDay(avg)
        }
    }, [onlinetime, characterData])

    async function updatePlayer(newPlayer) {
        setOnlinetime(0)
        setPlayer(newPlayer)
        setIsLoaded(false)
        if(newPlayer === "") {
            return
        }
        fetchAndSetData("/api/players/" + newPlayer, "Spieler konnten nicht geladen werden.", setUsers)
    }

    const loadPlayer = (playerToLoad) => {
        if(playerToLoad == null) {
            playerToLoad = player
        }
        if(playerToLoad === "") {
            return
        }

        setOnlinetime(0)
        fetchData("/api/onlinetime?player=" + playerToLoad, "Spielzeiten konnten nicht geladen werden.", data => setOnlinetime(data[0].online_time))

        setLastWeekTime(0)
        fetchData("/api/onlinetime?format=INT&player=" + playerToLoad + "&from=" + (Date.now() - WEEK_MILLIS), "Spielzeit der letzten Woche konnte nicht geladen werden.", data => setLastWeekTime(data.amount))

        setReputation(undefined)
        setExperience([])
        fetchData("/api/points/" + playerToLoad, "Punkte konnten nicht geladen werden.", data => {
            const points = experienceAndReputation(data)
            setReputation(points.reputation)
            setExperience(points.experience)
        })

        fetchAndSetData("/api/participant/" + playerToLoad, "Spielerdaten konnten nicht geladen werden.", setParticipantData)

        fetchAndSetData("/api/character/" + playerToLoad, "Charakterdaten konnten nicht geladen werden.", setCharacterData)

        fetchData("/api/mcstats?player=" + playerToLoad + "&stats=DEATHS,PLAYER_KILLS,MOB_KILLS", "Minecraft Statistiken konnten nicht geladen werden.", data => {
            setDeaths(data.DEATHS)
            setKills(data.PLAYER_KILLS)
            setMobKills(data.MOB_KILLS)
        })

        fetchData("/api/stats?player=" + playerToLoad + "&stats=Quest&format=INT", "Quest Statistiken konnten nicht geladen werden.", data => setSolvedQuests(data.amount))

        fetchData("/api/stats?player=" + playerToLoad + "&stats=Aktivität&format=INT", "Aktivität Statistiken konnten nicht geladen werden.", data => setSolvedActivities(data.amount))

        setIsLoaded(true)
    }

    return (
        <div className="row">
            <div className="col-12" style={{zIndex: "100", marginBottom: "20px"}}>
                <Autocomplete
                    wrapperStyle={{cursor: "pointer", float: "left"}}
                    getItemValue={(item) => item.label}
                    items={users.map(user => {
                        return {label: user}
                    })}
                    renderItem={(item, isHighlighted) =>
                        <div key={item.label} style={{ background: isHighlighted ? 'lightgray' : 'white' }}>
                            {item.label}
                        </div>
                    }
                    value={player}
                    onChange={(e) => updatePlayer(e.target.value)}
                    onSelect={(selected) => setPlayer(selected)}
                    renderMenu={renderSuggestionMenu}
                />
                <button type="submit" className="btn btn-primary" onClick={() => loadPlayer()}>Spieler laden</button>
            </div>

            {!isLoaded ? "" : 
            <>
            <div className="col-12">
                <div className="card card-primary">
                    <div className="card-header">
                        <h3>Charakter</h3>
                    </div>
                    <div className="card-body">
                        <div className="row">
                            <div className="col-12 col-sm-6 col-md-4 col-lg-3">
                                UUID: {participantData["uuid"] == null ? characterData.current == null ? "" : characterData.current.player_uuid : participantData["uuid"]}<br />
                                Name: {participantData["name"] == null ? characterData.current == null ? "" : characterData.current.player_name : participantData["name"]}<br />
                                Titel (Stand): {participantData["title"]} {participantData["state"] == null ? "" : "(" + participantData["state"] + ")"}<br />
                            </div>
                            <div className="col-12 col-sm-6 col-md-4 col-lg-3">
                                {characterData.current == null ? "" : 
                                <>Charaktername: {characterData.current.name}<br />
                                Geschlecht: {characterData.current.geschlecht}<br />
                                Alter: {characterData.current.alter}<br />
                                Aussehen: {characterData.current.aussehen}<br /></>}
                            </div>
                            <div className="col-12 col-sm-6 col-md-4 col-lg-3">
                                Gefolge: {participantData["faction_name"]}<br />
                                Rang: {participantData["rank"]}<br />
                                Geld: {participantData["money"]}<br />
                                Steuerverwarnungen: {participantData["warns"]}<br />
                            </div>
                            <div className="col-12 col-sm-6 col-md-4 col-lg-3">
                                Ritter: {participantData["is_knight"] ? "Ja" : "Nein"}<br />
                                Verfolgt: {participantData["is_hunted"] ? "Ja" : "Nein"}<br />
                                Eingesperrt: {participantData["is_jailed"] ? "Ja (" + participantData["jail_faction_name"] + ")" : "Nein"}<br />
                                Priester: {participantData["is_priest"] ? "Ja" : "Nein"}<br />
                            </div>
                        </div>
                        <br />
                        <div className="row">
                            <div className="col-12 col-sm-6 col-md-4 col-lg-3">
                                Tode: {deaths}<br />
                                Tötungen: {kills}<br />
                                NPC-Tötungen: {mobKills}<br />
                            </div>
                            <div className="col-12 col-sm-6 col-md-4 col-lg-3">
                                Spielzeit: {playTime}<br />
                                Ø Tägliche Spielzeit: {averagePlayTimePerDay}<br />
                                Spielzeit der letzten 7 Tage: {formatDuration(lastWeekTime)}<br />
                                {characterData.current == null ? "" : <>Spielt seit: {new Date(characterData.current.first_played).toLocaleDateString("de-DE", { year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric' }) + " Uhr"}<br /></>}
                                {characterData.current == null ? "" : <>Zuletzt gespielt: {new Date(characterData.current.last_played).toLocaleDateString("de-DE", { year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric' }) + " Uhr"}<br /></>}
                            </div>
                            <div className="col-12 col-sm-6 col-md-4 col-lg-3">
                                Erledigte Quests: {solvedQuests}<br />
                                Erledigte Aktivitäten: {solvedActivities}<br />
                                Ruf: {reputation === undefined ? "-" : reputation}<br />
                            </div>
                        </div>
                        {experience.length === 0 ? "" :
                        <div className="row">
                            <div className="col-12">
                                <br />
                                <h5>Erfahrung je Fertigkeit</h5>
                                <Bar options={{ indexAxis: "y", plugins: { legend: { display: false } } }} data={{
                                    labels: experience.map(entry => entry.skill),
                                    datasets: [{ label: "Erfahrung", data: experience.map(entry => entry.points), backgroundColor: "#3c8dbc" }],
                                }} />
                            </div>
                        </div>}
                    </div>
                </div>
            </div>
            <div className="col-12">
                <div className="card card-primary">
                    <div className="card-header">
                        <h3>Alte Charaktere</h3>
                    </div>
                    <div className="card-body">
                        <CharacterHistoryList filter={player} />
                    </div>
                </div>
            </div>
            <div className="col-12">
                <div className="card card-primary">
                    <div className="card-header">
                        <h3>Clients</h3>
                    </div>
                    <div className="card-body">
                        <ClientList filter={player} />
                    </div>
                </div>
            </div>
            <div className="col-12">
                <div className="card card-primary">
                    <div className="card-header">
                        <h3>Verwarnungen</h3>
                    </div>
                    <div className="card-body">
                        <PunishmentList filter={player} />
                    </div>
                </div>
            </div>
            <div className="col-12">
                <div className="card card-primary">
                    <div className="card-header">
                        <h3>IP Adressen</h3>
                    </div>
                    <div className="card-body">
                        <AddressList filter={player} />
                    </div>
                </div>
            </div>
            <div className="col-12">
                <div className="card card-primary">
                    <div className="card-header">
                        <h3>Spieler mit gleicher IP Adresse</h3>
                    </div>
                    <div className="card-body">
                        <SharedAddressList filter={player} />
                    </div>
                </div>
            </div>
            <div className="col-12">
                <div className="card card-primary">
                    <div className="card-header">
                        <h3>Tickets</h3>
                    </div>
                    <div className="card-body">
                        <TicketList filter={player} />
                    </div>
                </div>
            </div>
            <div className="col-12">
                <div className="card card-primary">
                    <div className="card-header">
                        <h3>Punkte</h3>
                    </div>
                    <div className="card-body">
                        <PointsList filter={player} />
                    </div>
                </div>
            </div>
            </>
            }
        </div>
    )
}
   
export default PlayerSearch